import * as PIXI from "pixi.js";
import { DeviceUtil } from "../../util/device/DeviceUtil";
import { EventHandler } from "../../util/event/EventHandler";
import { Timer } from "../../util/time/Timer";
import { DestroyUtil } from "../../util/view/DestroyUtil";
import { GraphicsUtil } from "../../util/view/GraphicsUtil";
import { MouseUtil } from "../../util/view/MouseUtil";
import { ViewUtils } from "../../util/view/ViewUtils";
import { AssetManager } from "../manager/AssetManager";
import { LanguageManager } from "../manager/LanguageManager";
import { OrientationType } from "../renderer/constant/RendererConstants";
import { AbstractControl } from "./AbstractControl";
import { TitanClip } from "./TitanClip";
import { ButtonConstants, ButtonConstantsNamed, ControlConstants, } from "./constant/ControlConstants";
/**
 * TitanButton.ts
 *
 * A button with 4-8 different image states and a text field.
 *
 * Can be held down for multiple presses or added to a radio-button style list.
 */
export class TitanButton extends AbstractControl {
    /**
     * Wrapper for a constructor call.
     * @param parent - What to attach the button as a child of.
     * @param properties - Landscape properties.
     * @param portraitProperties - Portrait properties.
     * @param clickHandler - `Function` called when the button is clicked.
     * @returns The new `TitanButton` instance.
     */
    static createButton(parent, properties, portraitProperties = undefined, clickHandler) {
        let button;
        if (properties) {
            button = new TitanButton(properties, portraitProperties);
            button.onClick(clickHandler, parent ? parent : this);
            parent && parent.addChild(button);
        }
        return button;
    }
    static showButton(button) {
        if (button) {
            this.enableButton(button);
            button.show();
        }
    }
    static hideButton(button) {
        if (button) {
            this.disableButton(button);
            button.hide();
        }
    }
    static disableButton(button) {
        if (button) {
            button.setEnabled(false);
        }
    }
    static enableButton(button) {
        if (button) {
            button.setEnabled(true);
        }
    }
    createProperties(landscapeProperties, portraitProperties) {
        super.createProperties(landscapeProperties, portraitProperties);
        this.landscapeProperties = landscapeProperties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    create() {
        this.enabled = true;
        this.selected = false;
        this.over = false;
        this.eventMode = "static";
        this.downStarted = false;
        this.upStarted = false;
        this.createButtonBackgrounds();
        this.createTextField();
        this.createRepeatTimer();
        this.createHitAreas();
    }
    createAccessibility() {
        this.accessible = true;
        this.accessibleChildren = this.uiProperties.accessibleOptions?.accessibleChildren ?? true;
        const hintDefault = this.uiProperties.accessibleOptions?.accessibleHint;
        const hintAlt = this.uiProperties.textFieldProperties?.langKey ? LanguageManager.getInstance().getLang(this.uiProperties.textFieldProperties?.langKey) : this.textField?.getText();
        this.accessibleHint = hintDefault || hintAlt || "";
        this.accessibleTitle = this.uiProperties.accessibleOptions?.accessibleTitle ?? "";
        this.accessibleType = this.uiProperties.accessibleOptions?.accessibleTitle ?? "button";
        this.tabIndex = this.uiProperties.accessibleOptions?.tabIndex ?? 0;
    }
    destroy() {
        super.destroy();
        DestroyUtil.destroy(this.textField);
        DestroyUtil.destroy(this.button);
        DestroyUtil.destroy(this.repeatTimer);
        this.landscapeProperties = null;
        this.portraitProperties = null;
        this.button = null;
        this.textField = null;
        this.repeatTimer = null;
        this.data = null;
    }
    /**
     * Button sprites
     */
    createButtonBackgrounds() {
        if (AssetManager.getInstance().hasFrameId(this.landscapeProperties.movieClipProperties.id +
            ButtonConstantsNamed.DEFAULT, this.landscapeProperties.movieClipProperties.spriteSheet)) {
            this.landscapeProperties.movieClipProperties.frameNames = [
                ButtonConstantsNamed.DEFAULT,
                ButtonConstantsNamed.ROLLOVER,
                ButtonConstantsNamed.PRESSED,
                ButtonConstantsNamed.DISABLED,
                ButtonConstantsNamed.SELECTED_DEFAULT,
                ButtonConstantsNamed.SELECTED_ROLLOVER,
                ButtonConstantsNamed.SELECTED_PRESSED,
                ButtonConstantsNamed.SELECTED_DISABLED,
            ];
        }
        if (this.portraitProperties?.movieClipProperties &&
            AssetManager.getInstance().hasFrameId(this.portraitProperties.movieClipProperties.id +
                ButtonConstantsNamed.DEFAULT, this.portraitProperties.movieClipProperties.spriteSheet)) {
            this.portraitProperties.movieClipProperties.frameNames = [
                ButtonConstantsNamed.DEFAULT,
                ButtonConstantsNamed.ROLLOVER,
                ButtonConstantsNamed.PRESSED,
                ButtonConstantsNamed.DISABLED,
                ButtonConstantsNamed.SELECTED_DEFAULT,
                ButtonConstantsNamed.SELECTED_ROLLOVER,
                ButtonConstantsNamed.SELECTED_PRESSED,
                ButtonConstantsNamed.SELECTED_DISABLED,
            ];
        }
        this.button = TitanClip.createMovieClip(this, this.landscapeProperties.movieClipProperties, this.portraitProperties?.movieClipProperties);
    }
    /**
     * Embedded text field
     */
    createTextField() {
        this.textField = this.createField(this.landscapeProperties.textFieldProperties, this.portraitProperties?.textFieldProperties);
        this.updateAlign();
    }
    /**
     * Wrapper for TitanText.createField
     */
    createField(properties, portraitProperties) {
        let textField;
        if (properties) {
            properties.horizontalAlign =
                properties.horizontalAlign || ControlConstants.CENTER;
            if (portraitProperties) {
                portraitProperties.horizontalAlign =
                    portraitProperties.horizontalAlign ||
                        ControlConstants.CENTER;
            }
            textField = ViewUtils.createField(this, properties, portraitProperties);
        }
        return textField;
    }
    createRepeatTimer() {
        if (this.uiProperties.repeat) {
            this.repeatTimer = new Timer(250);
            this.repeatTimer.onUpdate(this.handleRepeatTimerUpdate, this);
        }
    }
    createHitAreas() {
        this.button.forceRotate(OrientationType.LANDSCAPE);
        this.landscapeHitArea = this.createHitArea(this.landscapeProperties, this.button);
        if (this.hasPortraitProperties) {
            this.button.forceRotate(OrientationType.PORTRAIT);
            this.portraitHitArea = this.createHitArea(this.portraitProperties, this.button);
        }
        this.forceRotate(this.renderer.getOrientation());
    }
    createHitArea(properties, button) {
        //RM: let's keep circleRadius for backwards compatibility, but it's a pointless oversimplification. Just create a PIXI.IHitArea in the properties.
        let radius = properties.circleRadius;
        if (radius > 0) {
            let x = Math.round(button.width / 2);
            let y = Math.round(button.height / 2);
            return new PIXI.Circle(x, y, radius);
        }
        else if (properties.hitArea) {
            return properties.hitArea;
        }
    }
    addEventListeners() {
        super.addEventListeners();
        MouseUtil.setInteractive(this, true);
        MouseUtil.addMouseOver(this, this.handleMouseOver, this);
        MouseUtil.addMouseOut(this, this.handleMouseOut, this);
        MouseUtil.addMouseDown(this, this.handleMouseDown, this);
        MouseUtil.addMouseUp(this, this.handleMouseUpDelegate, this);
        MouseUtil.addMouseClick(this, this.handleMouseClick, this);
    }
    /**
     * @param value - `true` enables, `false` disables.
     */
    setEnabled(value) {
        if (this.enabled != value) {
            this.over = false;
            this.enabled = value;
            MouseUtil.setInteractive(this, value);
            this.stopRepeatTimer();
        }
        let state;
        if (this.uiProperties.isToggle && this.selected) {
            state = value
                ? ButtonConstants.SELECTED_DEFAULT
                : ButtonConstants.SELECTED_DISABLED;
        }
        else {
            state = value ? ButtonConstants.DEFAULT : ButtonConstants.DISABLED;
        }
        this.updateState(state);
        this.updateAlpha(value);
    }
    setSelected(value) {
        if (this.group) {
            this.group.setSelected(this, value);
            this.selected = this.group.isSelected(this);
        }
        else {
            this.selected = value;
        }
        this.updateState(this.selected
            ? ButtonConstants.SELECTED_DEFAULT
            : ButtonConstants.DEFAULT);
    }
    /**
     * Show localised text.
     * @param key - Lang constant.
     * @param refreshStyle - Default `false`.
     */
    setLangKey(key, refreshStyle = false) {
        this.textField.setLangKey(key, refreshStyle);
        this.updateAlign();
    }
    /**
     * Show this text.
     * @param text - Formatted string.
     * @param refreshStyle - Default `false`.
     */
    setText(text, refreshStyle = false) {
        this.textField.setText(text, refreshStyle);
        this.updateAlign();
    }
    /**
     * Recalculate hitArea and textField positions.
     * @param forceOrientation - Optional {@link OrientationType}.
     */
    updateAlign(forceOrientation) {
        if (this.hasPortraitProperties) {
            const isLandscape = this.renderer.isLandscape(forceOrientation);
            this.hitArea = isLandscape
                ? this.landscapeHitArea
                : this.portraitHitArea;
        }
        else {
            this.hitArea = this.landscapeHitArea;
        }
        if (this.textField && !this.uiProperties.disableAlign) {
            let x = Math.round(this.button.width / 2 + this.uiProperties.textFieldOffset.x);
            let y = Math.round((this.button.height - this.textField.getHeight()) / 2 +
                this.uiProperties.textFieldOffset.y);
            this.textField.setPosition(new PIXI.Point(x, y));
        }
    }
    startRepeatTimer(interval) {
        this.repeatTimer.reset(interval);
        this.repeatTimer.start();
    }
    stopRepeatTimer() {
        if (this.repeatTimer) {
            this.repeatTimer.stop();
        }
    }
    handleMouseDown() {
        if (this.enabled) {
            this.downStarted = true;
            this.updateState(this.selected
                ? ButtonConstants.SELECTED_PRESSED
                : ButtonConstants.PRESSED);
            this.callHandler(this.mouseDownHandler);
            if (this.uiProperties.repeat) {
                this.startRepeatTimer(250);
                this.handleClick();
            }
        }
    }
    /**
     * Validate the mouse is still on top of the button.
     */
    handleMouseUpDelegate(event) {
        if (this.downStarted) {
            this.downStarted = false;
            this.upStarted = true;
            gsap.delayedCall(0.1, () => {
                this.upStarted = false;
            });
            const localClick = this.toLocal(event.global);
            this.over = this.hitArea
                ? this.hitArea.contains(localClick.x, localClick.y)
                : GraphicsUtil.isInBounds(this, event.global);
            if (this.over) {
                event.originalEvent.stopImmediatePropagation();
            }
            this.handleMouseUp(false);
        }
    }
    /**
     * Attempt to perform a successful click.
     * @param force - Update states even if the mouse is in the wrong place.
     */
    handleMouseUp(force) {
        if (this.enabled) {
            let forceOrOver = force || this.over;
            if (forceOrOver && this.uiProperties.isToggle) {
                this.setSelected(!this.selected);
            }
            if (!DeviceUtil.isTouch() && forceOrOver) {
                this.updateState(this.selected
                    ? ButtonConstants.SELECTED_ROLLOVER
                    : ButtonConstants.ROLLOVER);
            }
            else {
                this.updateState(this.selected
                    ? ButtonConstants.SELECTED_DEFAULT
                    : ButtonConstants.DEFAULT);
            }
            if (forceOrOver) {
                if (!this.uiProperties.repeat) {
                    this.handleClick();
                }
            }
            this.stopRepeatTimer();
        }
    }
    handleMouseClick(event) {
        //only used by accessibility - keyboard navigation. Will not trigger unless a pointertap occurs with no preceding pointerdown/pointerup.
        if (!(this.upStarted || this.downStarted) && this.enabled) {
            if (this.uiProperties.isToggle) {
                this.setSelected(!this.selected);
            }
            this.handleClick();
        }
    }
    handleMouseOver() {
        if (this.enabled) {
            this.over = true;
            this.updateState(this.selected
                ? ButtonConstants.SELECTED_ROLLOVER
                : ButtonConstants.ROLLOVER);
            this.callHandler(this.mouseOverHandler);
        }
    }
    handleMouseOut() {
        if (this.enabled) {
            this.over = false;
            this.updateState(this.selected
                ? ButtonConstants.SELECTED_DEFAULT
                : ButtonConstants.DEFAULT);
            this.callHandler(this.mouseOutHandler);
        }
    }
    handleRepeatTimerUpdate() {
        let currentCount = this.repeatTimer.getCurrentCount();
        if (currentCount == 3) {
            this.startRepeatTimer(75);
        }
        this.handleClick();
    }
    handleClick() {
        if (this.enabled) {
            this.callHandler(this.clickHandler, this);
        }
    }
    handleResize(event) {
        super.handleResize(event);
        this.updateAlign();
    }
    handleRotate(event) {
        super.handleRotate(event);
        const data = event?.getCurrentTarget() || {};
        this.updateAlign(data.forceOrientation);
    }
    updateState(state) {
        this.state = state;
        if (this.uiProperties.textFieldProperties &&
            this.uiProperties.selectedTextFieldProperties) {
            let font = this.uiProperties.textFieldProperties.fontProperties;
            let validState = this.isValidState(state);
            if (validState) {
                font =
                    this.uiProperties.selectedTextFieldProperties
                        .fontProperties;
            }
            this.textField.setStyle(font);
        }
        this.button.gotoAndStop(state);
    }
    forceUnpressedState() {
        switch (this.state) {
            case ButtonConstants.DEFAULT:
            case ButtonConstants.ROLLOVER:
            case ButtonConstants.PRESSED:
                this.updateState(ButtonConstants.DEFAULT);
                break;
            case ButtonConstants.SELECTED_DEFAULT:
            case ButtonConstants.SELECTED_ROLLOVER:
            case ButtonConstants.SELECTED_PRESSED:
                this.updateState(ButtonConstants.SELECTED_DEFAULT);
                break;
            case ButtonConstants.DISABLED:
            case ButtonConstants.SELECTED_DISABLED:
                break;
        }
    }
    updateAlpha(value) {
        if (this.textField) {
            this.textField.alpha = value
                ? 1
                : this.uiProperties.disableTextAlpha;
        }
    }
    setData(data) {
        this.data = data;
    }
    setGroup(value) {
        this.group = value;
    }
    getGroup() {
        return this.group;
    }
    getData() {
        return this.data;
    }
    isSelected() {
        return this.selected;
    }
    isOver() {
        return this.over;
    }
    isEnabled() {
        return this.enabled;
    }
    isValidState(state) {
        return (state == ButtonConstants.PRESSED ||
            state == ButtonConstants.SELECTED_PRESSED ||
            state == ButtonConstants.SELECTED_DEFAULT ||
            state == ButtonConstants.SELECTED_ROLLOVER ||
            state == ButtonConstants.SELECTED_DISABLED);
    }
    getMovieClip() {
        return this.button;
    }
    getTextField() {
        return this.textField;
    }
    getWidth() {
        return this.button.width;
    }
    getHeight() {
        return this.button.height;
    }
    onMouseDown(handler, scope) {
        this.mouseDownHandler = new EventHandler(handler, scope);
    }
    onMouseOver(handler, scope) {
        this.mouseOverHandler = new EventHandler(handler, scope);
    }
    onMouseOut(handler, scope) {
        this.mouseOutHandler = new EventHandler(handler, scope);
    }
    onClick(handler, scope) {
        this.clickHandler = new EventHandler(handler, scope);
    }
    show() {
        super.show();
        this.updateAlign();
    }
    resetOverState() {
        this.over = false;
        this.updateState(this.selected
            ? ButtonConstants.SELECTED_DEFAULT
            : ButtonConstants.DEFAULT);
    }
}
//# sourceMappingURL=TitanButton.js.map